/**
 * Meilisearch API client module
 * Handles all communication with the Meilisearch server
 */

import type { MeiliConfig, MeiliSearchOptions, MeiliSearchResponse } from "../types/meilisearch";

// Cache for search results (1 minute TTL)
const searchCache = new Map<string, { data: MeiliSearchResponse; timestamp: number }>();
const CACHE_TTL = 60000; // 1 minute
let cachedConfig: MeiliConfig | null | undefined;

function isMeiliConfig(value: unknown): value is MeiliConfig {
  return (
    typeof value === "object" &&
    value !== null &&
    "enabled" in value &&
    typeof (value as MeiliConfig).enabled === "boolean"
  );
}

function readConfigFromJsonScript(): MeiliConfig | null {
  const configElement = document.getElementById("meili-config-data");
  if (!configElement?.textContent) return null;

  try {
    const parsed = JSON.parse(configElement.textContent) as unknown;
    return isMeiliConfig(parsed) ? parsed : null;
  } catch (error) {
    console.warn("[Meilisearch] Invalid configuration JSON", error);
    return null;
  }
}

/**
 * Get Meilisearch configuration from the JSON config script.
 * Falls back to window.MEILI_CONFIG for compatibility with older theme output.
 */
export function getMeiliConfig(): MeiliConfig | null {
  if (cachedConfig !== undefined) {
    return cachedConfig && cachedConfig.enabled ? cachedConfig : null;
  }

  const config = readConfigFromJsonScript() ?? window.MEILI_CONFIG ?? null;
  cachedConfig = config;

  if (!config || !config.enabled) {
    return null;
  }
  return config;
}

/**
 * Check if Meilisearch is enabled
 */
export function isMeiliEnabled(): boolean {
  const config = getMeiliConfig();
  return config !== null && config.enabled === true;
}

/**
 * Generate cache key from search options
 */
function getCacheKey(options: MeiliSearchOptions): string {
  return JSON.stringify(options);
}

/**
 * Get cached response if valid
 */
function getCached(key: string): MeiliSearchResponse | null {
  const cached = searchCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  if (cached) {
    searchCache.delete(key);
  }
  return null;
}

/**
 * Store response in cache
 */
function setCache(key: string, data: MeiliSearchResponse): void {
  searchCache.set(key, { data, timestamp: Date.now() });

  // Limit cache size to prevent memory issues
  if (searchCache.size > 50) {
    const firstKey = searchCache.keys().next().value;
    if (firstKey) {
      searchCache.delete(firstKey);
    }
  }
}

/**
 * Perform a search against Meilisearch
 */
export async function meiliSearch(
  options: MeiliSearchOptions
): Promise<MeiliSearchResponse | null> {
  const config = getMeiliConfig();
  if (!config || !config.host || !config.searchKey || !config.index) {
    console.warn("[Meilisearch] Missing configuration");
    return null;
  }

  // Check cache first
  const cacheKey = getCacheKey(options);
  const cached = getCached(cacheKey);
  if (cached) {
    return cached;
  }

  const url = `${config.host}/indexes/${config.index}/search`;

  const body: Record<string, unknown> = {
    q: options.query,
    limit: options.limit ?? config.previewLimit ?? 6,
    offset: options.offset ?? 0
  };

  if (options.filter) {
    body.filter = options.filter;
  }

  if (options.facets) {
    body.facets = options.facets;
  }

  if (options.attributesToRetrieve) {
    body.attributesToRetrieve = options.attributesToRetrieve;
  }

  if (options.attributesToHighlight) {
    body.attributesToHighlight = options.attributesToHighlight;
    body.highlightPreTag = options.highlightPreTag ?? "<mark>";
    body.highlightPostTag = options.highlightPostTag ?? "</mark>";
  }

  if (options.sort) {
    body.sort = options.sort;
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${config.searchKey}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      console.error("[Meilisearch] Search failed:", response.status, response.statusText);
      return null;
    }

    const data = (await response.json()) as MeiliSearchResponse;

    // Cache the result
    setCache(cacheKey, data);

    return data;
  } catch (error) {
    console.error("[Meilisearch] Search error:", error);
    return null;
  }
}

/**
 * Format price in EUR with proper formatting
 */
export function formatPrice(priceInCents: number): string {
  const euros = priceInCents / 100;
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR"
  }).format(euros);
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Build product URL from handle
 */
export function getProductUrl(handle: string): string {
  return `/products/${handle}`;
}

/**
 * Get product image URL or placeholder
 * Handles both image_url (string) and images (array) formats
 */
export function getProductImageUrl(
  imageUrl: string | undefined,
  images: { src: string }[] | undefined,
  size = "200x200"
): string {
  // Extract width from size string (e.g. "120x120" → 120, "200x200" → 200)
  const width = parseInt(size.split("x")[0], 10) || 200;

  // First try image_url (Meilisearch format)
  if (imageUrl) {
    if (imageUrl.includes("cdn.shopify.com")) {
      return appendWidthParam(imageUrl, width);
    }
    return imageUrl;
  }

  // Fallback to images array
  if (images && images.length > 0) {
    const src = images[0].src;
    if (src.includes("cdn.shopify.com")) {
      return appendWidthParam(src, width);
    }
    return src;
  }

  return `https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png`;
}

/**
 * Append width param to Shopify CDN URL (modern format)
 */
function appendWidthParam(url: string, width: number): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}width=${width}`;
}

/**
 * Debounce function for search input
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  };
}

/**
 * Clear the search cache
 */
export function clearCache(): void {
  searchCache.clear();
}

/**
 * Finish keywords for matching variants
 */
const finishKeywords: Record<string, string[]> = {
  bronze: ["bronze", "bronze finish"],
  silver: ["silver", "silver finish"],
  gold: ["gold", "gold finish"]
};

/**
 * Get the current selected finish from localStorage
 */
export function getSelectedFinish(): string {
  try {
    return localStorage.getItem("remerlinds_preferred_finish") || "bronze";
  } catch {
    return "bronze";
  }
}

/**
 * Find the price for a specific finish variant
 * @param variants - Array of product variants
 * @param finish - The finish to find (bronze, silver, gold)
 * @returns The variant price or null if not found
 */
export function getFinishPrice(
  variants:
    | Array<{
        title?: string;
        price?: number;
        option1?: string;
        option2?: string;
        option3?: string;
      }>
    | undefined,
  finish: string
): number | null {
  if (!variants || variants.length === 0) return null;

  const keywords = finishKeywords[finish] || finishKeywords["bronze"];

  for (const variant of variants) {
    // Check title and all option fields
    const searchFields = [
      variant.title?.toLowerCase() || "",
      variant.option1?.toLowerCase() || "",
      variant.option2?.toLowerCase() || "",
      variant.option3?.toLowerCase() || ""
    ];

    for (const field of searchFields) {
      if (keywords.some((keyword) => field.includes(keyword))) {
        return variant.price ?? null;
      }
    }
  }

  // If no match found, return the first variant's price
  return variants[0]?.price ?? null;
}
